/**
 * @file inward-po-summary.tsx
 * @module app/modules/inward/components/inward-po-summary
 * @description PO summary on the PO details page (old UProcure and Procurement (New)), for POs
 *   with at least one GRN: PO value vs value received across all GRNs, and per product + unit
 *   the ordered, received, missing, damaged and remaining quantities with their values; per GRN
 *   what was shipped, what is deducted (missing + damaged) and what is payable. Payable is the
 *   received (good) quantity x cost: the amount the Vendor Ledger counts. Built by the backend
 *   (`poSummary` on the PO details response).
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.4.0
 * @version 1.4.0
 */

import { Card, Table } from "react-bootstrap";
import Method from "../../../../utils/methods";
import { unitLabel } from "../utils/inward-po";
import { formatDateTime } from "../utils/inward-timeline";
import "../styles/inward.css";

export interface PoSummaryLine {
  variant: string;
  title: string;
  type: number;
  ordered: number;
  received: number;
  missing: number;
  damaged: number;
  rejectedOther: number;
  remaining: number;
  cost: number;
  orderedValue: number;
  receivedValue: number;
  missingValue: number;
  damagedValue: number;
  remainingValue: number;
}
export interface PoSummaryGrn {
  refKey: string;
  date: string | null;
  invoiceNumber: string;
  received: number;
  missing: number;
  damaged: number;
  shippedValue: number;
  deductions: number;
  payable: number;
}
export interface PoSummaryData {
  lines: PoSummaryLine[];
  grns: PoSummaryGrn[];
  totals: {
    ordered: number;
    received: number;
    missing: number;
    damaged: number;
    rejectedOther: number;
    remaining: number;
    orderedValue: number;
    receivedValue: number;
    missingValue: number;
    damagedValue: number;
    remainingValue: number;
    payableValue: number;
  };
  /** GRN rejections and missing / damaged decisions on this PO, newest first */
  log?: PoGrnLogEntry[];
}
export interface PoGrnLogEntry {
  action: "grnRejected" | "shortfallApproved" | "shortfallRejected" | string;
  grnRefKey?: string;
  stage?: string;
  note?: string;
  quantities?: { title?: string; quantity?: number; detail?: string }[];
  by?: { name?: string };
  at?: string;
}

const LOG_LABELS: Record<string, string> = {
  grnRejected: "GRN rejected · all quantities back to the PO",
  shortfallApproved: "Missing / damaged approved (closed)",
  shortfallRejected: "Missing / damaged rejected · back to the PO",
};

/** PO-level log of GRN rejections and missing / damaged decisions. */
const GrnLog = ({ log }: { log: PoGrnLogEntry[] }) => (
  <div className="mt-6" data-testid="po-summary-log">
    <h3 className="fs-18 fw-bolder mb-3">GRN rejections and missing / damaged decisions</h3>
    <ul className="list-unstyled mb-0">
      {log.map((e, i) => (
        <li key={`${e.at}-${i}`} className="border-bottom py-3 fs-14">
          <div className="fw-bold">
            GRN {e.grnRefKey || "-"} · {LOG_LABELS[e.action] ?? e.action}
          </div>
          <div className="text-gray-700">
            {e.by?.name || "-"}
            {e.stage ? ` (${e.stage === "finance" ? "Finance" : "Warehouse"})` : ""} · {formatDateTime(e.at)}
            {e.note ? ` · ${e.note}` : ""}
          </div>
          {(e.quantities ?? []).map((q, j) => (
            <div key={j}>
              {q.title || "-"}: {qty(q.quantity ?? 0)}
              {q.detail ? ` (${q.detail})` : ""}
            </div>
          ))}
        </li>
      ))}
    </ul>
  </div>
);

const money = (n: number) => `TSh ${Method.getGeneralizedAmount(n)}`;
const qty = (n: number) => Number(n || 0).toLocaleString("en-US");

const Figure = ({ label, count, value, testId, tone = "" }: { label: string; count: number; value: number; testId: string; tone?: string }) => (
  <div className={`border rounded p-4 flex-fill ${tone}`} style={{ minWidth: 170 }} data-testid={testId}>
    <div className="fs-14 text-gray-700 fw-600">{label}</div>
    <div className="fs-20 fw-bolder mt-1">{money(value)}</div>
    <div className="fs-14 text-gray-700">{qty(count)} units</div>
  </div>
);

const InwardPoSummary = ({ summary }: { summary?: PoSummaryData | null }) => {
  const log = summary?.log ?? [];
  if (!summary || (!summary.grns?.length && !log.length)) return null;
  if (!summary.grns?.length) {
    // every GRN was rejected: only the log is left
    return (
      <Card className="border border-r10px mb-7" data-testid="po-summary">
        <Card.Body className="pt-1 pb-6">
          <GrnLog log={log} />
        </Card.Body>
      </Card>
    );
  }
  const t = summary.totals;
  return (
    <Card className="border border-r10px mb-7" data-testid="po-summary">
      <Card.Body className="pt-7 pb-6">
        <h2 className="fs-22 fw-bolder mb-1">PO summary</h2>
        <div className="fs-14 text-gray-700 mb-5">
          Across {summary.grns.length} GRN{summary.grns.length === 1 ? "" : "s"}. Payable = good quantity received x cost; this is
          what the Vendor Ledger counts. Missing and damaged goods are deducted.
        </div>

        <div className="d-flex flex-wrap gap-3 mb-6">
          <Figure label="PO value (ordered)" count={t.ordered} value={t.orderedValue} testId="po-summary-ordered" />
          <Figure label="Received (payable)" count={t.received} value={t.payableValue} testId="po-summary-received" tone="bg-light-success" />
          <Figure label="Missing (deducted)" count={t.missing} value={t.missingValue} testId="po-summary-missing" tone={t.missing ? "bg-light-danger" : ""} />
          <Figure label="Damaged (deducted)" count={t.damaged} value={t.damagedValue} testId="po-summary-damaged" tone={t.damaged ? "bg-light-danger" : ""} />
          <Figure label="Remaining (not yet received)" count={t.remaining} value={t.remainingValue} testId="po-summary-remaining" tone={t.remaining ? "bg-light-warning" : ""} />
        </div>

        <h3 className="fs-18 fw-bolder mb-3">By product</h3>
        <div className="table-responsive mb-6">
          <Table className="table-row-bordered align-middle gs-3 gy-3 mb-0 fs-14" data-testid="po-summary-lines">
            <thead>
              <tr className="fw-bold text-dark">
                <th>Product</th>
                <th>Unit</th>
                <th className="text-end">Ordered</th>
                <th className="text-end">Received</th>
                <th className="text-end">Missing</th>
                <th className="text-end">Damaged</th>
                {t.rejectedOther > 0 && <th className="text-end">Rejected (other)</th>}
                <th className="text-end">Remaining</th>
                <th className="text-end">Cost</th>
                <th className="text-end">Ordered value</th>
                <th className="text-end">Received value</th>
              </tr>
            </thead>
            <tbody>
              {summary.lines.map((l) => (
                <tr key={`${l.variant}_${l.type}`}>
                  <td className="fw-600">{l.title || "-"}</td>
                  <td>{unitLabel(l.type)}</td>
                  <td className="text-end">{qty(l.ordered)}</td>
                  <td className="text-end">{qty(l.received)}</td>
                  <td className={`text-end ${l.missing ? "text-danger fw-bold" : ""}`}>{qty(l.missing)}</td>
                  <td className={`text-end ${l.damaged ? "text-danger fw-bold" : ""}`}>{qty(l.damaged)}</td>
                  {t.rejectedOther > 0 && <td className="text-end">{qty(l.rejectedOther)}</td>}
                  <td className={`text-end ${l.remaining ? "text-warning fw-bold" : ""}`}>{qty(l.remaining)}</td>
                  <td className="text-end">{money(l.cost)}</td>
                  <td className="text-end">{money(l.orderedValue)}</td>
                  <td className="text-end">{money(l.receivedValue)}</td>
                </tr>
              ))}
              <tr className="fw-bolder">
                <td colSpan={2}>Total</td>
                <td className="text-end">{qty(t.ordered)}</td>
                <td className="text-end">{qty(t.received)}</td>
                <td className="text-end">{qty(t.missing)}</td>
                <td className="text-end">{qty(t.damaged)}</td>
                {t.rejectedOther > 0 && <td className="text-end">{qty(t.rejectedOther)}</td>}
                <td className="text-end">{qty(t.remaining)}</td>
                <td />
                <td className="text-end">{money(t.orderedValue)}</td>
                <td className="text-end">{money(t.receivedValue)}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        <h3 className="fs-18 fw-bolder mb-3">By GRN (invoice adjusted for missing and damaged)</h3>
        <div className="table-responsive">
          <Table className="table-row-bordered align-middle gs-3 gy-3 mb-0 fs-14" data-testid="po-summary-grns">
            <thead>
              <tr className="fw-bold text-dark">
                <th>GRN</th>
                <th>Date</th>
                <th>Invoice no.</th>
                <th className="text-end">Received</th>
                <th className="text-end">Missing</th>
                <th className="text-end">Damaged</th>
                <th className="text-end">Shipped value</th>
                <th className="text-end">Deductions</th>
                <th className="text-end">Payable</th>
              </tr>
            </thead>
            <tbody>
              {summary.grns.map((g) => (
                <tr key={g.refKey}>
                  <td className="fw-600">{g.refKey || "-"}</td>
                  <td>{g.date ? formatDateTime(g.date) : "-"}</td>
                  <td>{g.invoiceNumber || "-"}</td>
                  <td className="text-end">{qty(g.received)}</td>
                  <td className="text-end">{qty(g.missing)}</td>
                  <td className="text-end">{qty(g.damaged)}</td>
                  <td className="text-end">{money(g.shippedValue)}</td>
                  <td className={`text-end ${g.deductions ? "text-danger fw-bold" : ""}`}>{g.deductions ? `- ${money(g.deductions)}` : money(0)}</td>
                  <td className="text-end fw-bold">{money(g.payable)}</td>
                </tr>
              ))}
              <tr className="fw-bolder">
                <td colSpan={6}>All GRNs</td>
                <td className="text-end">{money(summary.grns.reduce((s, g) => s + g.shippedValue, 0))}</td>
                <td className="text-end">- {money(summary.grns.reduce((s, g) => s + g.deductions, 0))}</td>
                <td className="text-end">{money(summary.grns.reduce((s, g) => s + g.payable, 0))}</td>
              </tr>
            </tbody>
          </Table>
        </div>
        {log.length > 0 && <GrnLog log={log} />}
      </Card.Body>
    </Card>
  );
};

export default InwardPoSummary;
